import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import {
  useQuery,
  useMutation,
  useSuspenseQuery,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import {
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Check,
  ChevronsUpDown,
  X,
  Facebook,
  Instagram,
  Youtube,
  Mail,
  Users,
  Rss,
  MessageSquare,
  Music2,
  Sparkles,
  Trophy,
  Crown,
  Rocket,
  Shield,
  Globe,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { COUNTRIES, COUNTRY_MAP } from "@/lib/countries";
import { LANGUAGES, LANGUAGE_MAP } from "@/lib/referral/languages";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

import {
  applyForReferral,
  getMyPartner,
  getReferralPublicSettings,
} from "@/lib/referral/referral.functions";
import {
  applyForReferralSchema,
  PROMO_CHANNELS,
  PAYOUT_METHODS,
  type ApplyForReferralInput,
} from "@/lib/referral/referral-schema";

/* ────────────────────────────────────────────────────────────────────── */
/* Route                                                                  */
/* ────────────────────────────────────────────────────────────────────── */

const settingsQuery = queryOptions({
  queryKey: ["referral", "public-settings"],
  queryFn: () => getReferralPublicSettings(),
  staleTime: 60_000,
});

export const Route = createFileRoute("/referral/apply")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) {
      throw redirect({ to: "/auth", search: { redirect: location.href } as any });
    }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(settingsQuery),
  head: () => ({
    meta: [
      { title: "Become a FIXHEN partner" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Apply to the FIXHEN partner programme — three tiers, up to 15% commission.",
      },
    ],
  }),
  component: ApplyPage,
});

/* ────────────────────────────────────────────────────────────────────── */
/* Channel + step config                                                  */
/* ────────────────────────────────────────────────────────────────────── */

const CHANNEL_META: Record<
  (typeof PROMO_CHANNELS)[number],
  { label: string; icon: React.ComponentType<{ className?: string }>; hue: string }
> = {
  facebook:  { label: "Facebook",             icon: Facebook,      hue: "from-[#1877F2] to-[#4267B2]" },
  instagram: { label: "Instagram",            icon: Instagram,     hue: "from-[#F58529] via-[#DD2A7B] to-[#8134AF]" },
  x_twitter: { label: "X (Twitter)",          icon: MessageSquare, hue: "from-[#111] to-[#333]" },
  tiktok:    { label: "TikTok",               icon: Music2,        hue: "from-[#25F4EE] to-[#FE2C55]" },
  youtube:   { label: "YouTube",              icon: Youtube,       hue: "from-[#FF0000] to-[#B31217]" },
  email:     { label: "Email / newsletter",   icon: Mail,          hue: "from-emerald-400 to-emerald-600" },
  friends:   { label: "Friends & family",     icon: Users,         hue: "from-amber-400 to-orange-500" },
  blog:      { label: "Blog / website",       icon: Rss,           hue: "from-sky-400 to-sky-600" },
  forum:     { label: "Forums / communities", icon: Users,         hue: "from-indigo-400 to-indigo-600" },
  other:     { label: "Other",                icon: Sparkles,      hue: "from-slate-400 to-slate-600" },
};

const STEPS = [
  { id: 1, label: "Who you are", hint: "Name, phone, country" },
  { id: 2, label: "Your reach",  hint: "Languages and promotion channels" },
  { id: 3, label: "Payouts",     hint: "How we should send your commissions" },
  { id: 4, label: "Review",      hint: "Confirmation and terms" },
] as const;

const STORAGE_KEY = "fixhen.referral.apply.draft.v2";

/** Format basis points as a Bulgarian-style percent: 1250 → "12,50%", 1000 → "10%". */
function formatBp(bp: number): string {
  const pct = bp / 100;
  const isWhole = Math.abs(pct - Math.round(pct)) < 0.001;
  return new Intl.NumberFormat("bg-BG", {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(pct) + "%";
}

/* ────────────────────────────────────────────────────────────────────── */
/* Page                                                                   */
/* ────────────────────────────────────────────────────────────────────── */

function ApplyPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fetchSettings = useServerFn(getReferralPublicSettings);
  const fetchPartner = useServerFn(getMyPartner);
  const submitApp = useServerFn(applyForReferral);

  const { data: s } = useSuspenseQuery({
    ...settingsQuery,
    queryFn: () => fetchSettings(),
  });

  const partnerQ = useQuery({
    queryKey: ["referral", "me"],
    queryFn: () => fetchPartner(),
  });

  const form = useForm<ApplyForReferralInput>({
    resolver: zodResolver(applyForReferralSchema) as any,
    mode: "onBlur",
    defaultValues: {
      first_name: "",
      last_name: "",
      phone: "",
      country: "BG",
      languages: ["bg"],
      promo_channels: [],
      pitch: "",
      payout_method: "bank",
      payout_details: {},
      accepted_terms_version: s.terms_version,
    },
  });

  // Draft persistence (session-only; clears on submit)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        form.reset({ ...form.getValues(), ...parsed, accepted_terms_version: s.terms_version });
      }
    } catch { /* noop */ }
    const sub = form.watch((v) => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...v, payout_details: v.payout_details }));
      } catch { /* noop */ }
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step, setStep] = useState(1);
  const [shake, setShake] = useState(false);

  const mut = useMutation({
    mutationFn: (values: ApplyForReferralInput) => submitApp({ data: values as any }),
    onSuccess: async () => {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      toast.success("Application submitted! We'll get back to you within 48 hours.");
      await qc.invalidateQueries({ queryKey: ["referral", "me"] });
      navigate({ to: "/account/referrals" as any });
    },
    onError: (e: any) => {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      toast.error(e?.message ?? "Something went wrong.");
    },
  });

  // Already a partner
  if (partnerQ.data?.partner) {
    const p = partnerQ.data.partner;
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-sky-50 to-white text-slate-900 flex items-center justify-center px-4">
        <div className="max-w-lg w-full rounded-3xl border border-sky-200/70 bg-white p-10 text-center backdrop-blur-xl">
          <CheckCircle2 className="mx-auto mb-6 h-16 w-16 text-emerald-400" />
          <h1 className="font-display text-3xl font-black">You're already in the programme</h1>
          <p className="mt-3 text-slate-600">
            Status: <span className="font-semibold uppercase">{p.status}</span>
            <br />
            Your code:{" "}
            <code className="rounded bg-sky-100 px-2 py-1 font-mono text-sm">{p.code}</code>
          </p>
          <div className="mt-8">
            <Link
              to={"/account/referrals" as any}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-black text-white hover:bg-slate-800"
            >
              Go to your dashboard <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  async function nextStep() {
    const stepFields: Record<number, (keyof ApplyForReferralInput | `payout_details.${string}`)[]> = {
      1: ["first_name", "last_name", "phone", "country"],
      2: ["languages", "promo_channels", "pitch"],
      3: ["payout_method"],
      4: ["accepted_terms_version"],
    };
    const ok = await form.trigger(stepFields[step] as any);
    if (!ok) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-sky-50 to-white text-slate-900 overflow-x-hidden">
      <MeshBackdrop />

      {/* top bar */}
      <div className="relative z-20 flex items-center justify-between px-6 py-6 sm:px-10">
        <Link
          to="/referral"
          className="inline-flex items-center gap-2 rounded-full border border-sky-200/70 bg-sky-50 px-4 py-2 text-xs font-semibold text-slate-700 backdrop-blur hover:bg-sky-100"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Link>
        <Link to="/" className="font-display text-lg font-black tracking-tight">
          FIX<span className="text-sky-600">HEN</span>
        </Link>
      </div>

      <div className="relative z-10 mx-auto grid max-w-7xl gap-10 px-6 pb-24 pt-4 sm:px-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-16">
        {/* LEFT — editorial */}
        <EditorialPanel step={step} settings={s} />

        {/* RIGHT — form card */}
        <div className={cn("relative", shake && "animate-[shake_0.5s_ease-in-out]")}>
          <div className="rounded-[2rem] border border-sky-200/70 bg-white/80 p-6 shadow-[0_40px_100px_-30px_rgba(14,116,189,0.18)] backdrop-blur-2xl sm:p-10">
            <StepIndicator step={step} />
            <form
              onSubmit={form.handleSubmit((v) => mut.mutate(v))}
              className="mt-10 space-y-8"
              noValidate
            >
              <div className="relative min-h-[420px]">
                <StepPanel active={step === 1}><Step1 form={form} /></StepPanel>
                <StepPanel active={step === 2}><Step2 form={form} /></StepPanel>
                <StepPanel active={step === 3}><Step3 form={form} /></StepPanel>
                <StepPanel active={step === 4}><Step4 form={form} settings={s} /></StepPanel>
              </div>

              <div className="flex items-center justify-between gap-4 border-t border-sky-200/70 pt-6">
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  disabled={step === 1}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-transparent px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>

                {step < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                  >
                    <span>Continue</span>
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={mut.isPending}
                    className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-emerald-600 px-8 py-4 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {mut.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Rocket className="h-4 w-4" />
                    )}
                    Submit application
                  </button>
                )}
              </div>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            Applications are reviewed within 48 hours. No joining fees.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes shake { 10%,90%{transform:translate3d(-1px,0,0)} 20%,80%{transform:translate3d(2px,0,0)} 30%,50%,70%{transform:translate3d(-4px,0,0)} 40%,60%{transform:translate3d(4px,0,0)} }
        @keyframes floaty { 0%,100%{transform:translate3d(0,0,0)} 50%{transform:translate3d(0,-14px,0)} }
        @keyframes drift { 0%{transform:translate3d(0,0,0) rotate(0deg)} 100%{transform:translate3d(0,0,0) rotate(360deg)} }
        @keyframes stepIn { from{opacity:0; transform:translateY(12px)} to{opacity:1; transform:translateY(0)} }
        @media (prefers-reduced-motion: reduce) {
          [class*="animate-"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Backdrop                                                               */
/* ────────────────────────────────────────────────────────────────────── */

function MeshBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-sky-200/40 blur-[140px]" />
      <div className="absolute bottom-0 right-0 h-[480px] w-[480px] rounded-full bg-slate-200/50 blur-[140px]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,#ffffff_85%)]" />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Editorial panel (left)                                                 */
/* ────────────────────────────────────────────────────────────────────── */

function EditorialPanel({
  step,
  settings,
}: {
  step: number;
  settings: Awaited<ReturnType<typeof getReferralPublicSettings>>;
}) {
  const tiers = [
    { icon: Shield, name: "Silver",   bp: settings.tier_silver_bp,   accent: "text-slate-600" },
    { icon: Trophy, name: "Gold",     bp: settings.tier_gold_bp,     accent: "text-amber-600" },
    { icon: Crown,  name: "Platinum", bp: settings.tier_platinum_bp, accent: "text-indigo-600" },
  ];
  return (
    <aside className="relative lg:sticky lg:top-10 lg:self-start">
      <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600">
        <Sparkles className="h-3 w-3 text-sky-600" /> Partner programme
      </p>
      <h1 className="font-display text-[clamp(2.2rem,4vw,3.6rem)] font-black leading-[1] tracking-tight text-slate-900">
        Apply to partner with FIXHEN.
      </h1>
      <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600">
        Three commission tiers up to <b className="text-slate-900">{formatBp(settings.tier_platinum_bp)}</b>{" "}
        of the net sale. Cookie window of{" "}
        <b className="text-slate-900">{settings.cookie_days} days</b>. Approval within 48 hours.
        No joining fees.
      </p>

      {/* Tier ladder */}
      <div className="mt-10 space-y-3">
        {tiers.map((t) => {
          const Icon = t.icon;
          return (
            <div
              key={t.name}
              className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4"
            >
              <div className={cn("grid h-10 w-10 place-items-center rounded-lg bg-slate-100", t.accent)}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-display text-lg font-bold text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500">{t.name} tier</div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-black tabular-nums text-slate-900">{formatBp(t.bp)}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">per sale</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* progress micro-copy */}
      <div className="mt-10 rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
          Step {step} of 4
        </div>
        <div className="mt-2 font-display text-xl font-bold text-slate-900">{STEPS[step - 1].label}</div>
        <div className="text-sm text-slate-500">{STEPS[step - 1].hint}</div>
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-slate-900 transition-[width] duration-500"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>
      </div>

      <div className="mt-8 hidden items-center gap-6 text-xs text-slate-400 lg:flex">
        <span className="flex items-center gap-2"><Shield className="h-4 w-4" /> GDPR compliant</span>
        <span className="flex items-center gap-2"><Rocket className="h-4 w-4" /> Approval within 48h</span>
      </div>
    </aside>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Step indicator                                                         */
/* ────────────────────────────────────────────────────────────────────── */

function StepIndicator({ step }: { step: number }) {
  return (
    <ol className="flex items-center justify-between gap-2">
      {STEPS.map((s) => {
        const done = s.id < step;
        const active = s.id === step;
        return (
          <li key={s.id} className="flex flex-1 items-center gap-2" aria-current={active ? "step" : undefined}>
            <div
              className={cn(
                "grid h-9 w-9 shrink-0 place-items-center rounded-full border text-xs font-black transition",
                done && "border-emerald-400/60 bg-emerald-400/20 text-emerald-200",
                active && "border-slate-900 bg-slate-900 text-white shadow-[0_0_0_4px_rgba(15,23,42,0.08)]",
                !done && !active && "border-sky-200 bg-sky-50 text-slate-400",
              )}
            >
              {done ? <Check className="h-4 w-4" /> : s.id}
            </div>
            {s.id < 4 && (
              <div className="h-px flex-1 bg-slate-200" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Step panel wrapper                                                     */
/* ────────────────────────────────────────────────────────────────────── */

function StepPanel({ active, children }: { active: boolean; children: React.ReactNode }) {
  if (!active) return null;
  return <div className="[animation:stepIn_0.5s_ease-out]">{children}</div>;
}

/* ────────────────────────────────────────────────────────────────────── */
/* STEP 1 — Identity                                                      */
/* ────────────────────────────────────────────────────────────────────── */

function Step1({ form }: { form: UseFormReturn<ApplyForReferralInput> }) {
  const country = form.watch("country");
  const errors = form.formState.errors;
  return (
    <div className="space-y-8">
      <StepHeader
        eyebrow="First details"
        title="What should we call you?"
        subtitle="We use your name only internally and for payouts. We will never display it publicly without your permission."
      />

      <div className="grid gap-6 sm:grid-cols-2">
        <UnderlineInput
          label="First name"
          required
          error={errors.first_name?.message}
          {...form.register("first_name")}
        />
        <UnderlineInput
          label="Last name"
          required
          error={errors.last_name?.message}
          {...form.register("last_name")}
        />
      </div>

      <PhoneField form={form} />

      <CountrySelect
        value={country}
        onChange={(v) => form.setValue("country", v, { shouldValidate: true })}
        error={errors.country?.message}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* STEP 2 — Reach                                                         */
/* ────────────────────────────────────────────────────────────────────── */

function Step2({ form }: { form: UseFormReturn<ApplyForReferralInput> }) {
  const languages = form.watch("languages") || [];
  const channels = form.watch("promo_channels") || [];
  const errors = form.formState.errors;
  const pitch = form.watch("pitch") || "";

  return (
    <div className="space-y-8">
      <StepHeader
        eyebrow="Reach"
        title="Who can you reach?"
        subtitle="This helps us understand your audience and send you the right promotional materials."
      />

      <FieldWrap label="Languages you speak" required error={errors.languages?.message as string | undefined}>
        <LanguageMultiSelect
          value={languages}
          onChange={(v) => form.setValue("languages", v, { shouldValidate: true })}
        />
      </FieldWrap>

      <FieldWrap
        label="Planned promotion channels"
        required
        hint="Pick all that you plan to use. We'll provide resources for each."
        error={errors.promo_channels?.message as string | undefined}
      >
        <ChannelChips
          value={channels}
          onChange={(v) => form.setValue("promo_channels", v as any, { shouldValidate: true })}
        />
      </FieldWrap>

      <FieldWrap
        label="How do you plan to promote us? (optional)"
        hint={`${pitch.length}/500 characters`}
        error={errors.pitch?.message}
      >
        <textarea
          {...form.register("pitch")}
          maxLength={500}
          rows={4}
          placeholder="e.g. I run YouTube reviews of classic Mercedes and plan to feature your parts…"
          className="w-full resize-none rounded-2xl border border-sky-200/70 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10"
        />
      </FieldWrap>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* STEP 3 — Payout                                                        */
/* ────────────────────────────────────────────────────────────────────── */

function Step3({ form }: { form: UseFormReturn<ApplyForReferralInput> }) {
  const method = form.watch("payout_method");

  const METHODS: Array<{
    id: (typeof PAYOUT_METHODS)[number];
    name: string;
    tagline: string;
    Logo: React.ComponentType<{ className?: string }>;
  }> = [
    { id: "bank",    name: "Bank transfer", tagline: "SEPA in EUR",       Logo: BankLogo },
    { id: "paypal",  name: "PayPal",        tagline: "Instant, via email", Logo: PayPalLogo },
    { id: "wise",    name: "Wise",          tagline: "Low FX fees",        Logo: WiseLogo },
    { id: "revolut", name: "Revolut",       tagline: "@tag or email",      Logo: RevolutLogo },
  ];

  return (
    <div className="space-y-8">
      <StepHeader
        eyebrow="Payouts"
        title="How should we send your money?"
        subtitle="Pick the method you prefer — we only need this for now. You will add the account details later from your partner dashboard, before the first payout."
      />

      <FieldWrap label="Preferred method" required>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {METHODS.map((m) => {
            const active = method === m.id;
            const Logo = m.Logo;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => form.setValue("payout_method", m.id, { shouldValidate: true })}
                aria-pressed={active}
                className={cn(
                  "relative flex flex-col items-start gap-3 overflow-hidden rounded-2xl border p-4 text-left transition",
                  active
                    ? "border-slate-900 bg-white shadow-[0_0_0_3px_rgba(15,23,42,0.08)]"
                    : "border-sky-200/70 bg-white/80 hover:bg-sky-50",
                )}
              >
                <div className="flex h-8 items-center">
                  <Logo className="h-6 w-auto max-w-[110px] text-slate-900" />
                </div>
                <div>
                  <div className="font-display text-sm font-bold text-slate-900">{m.name}</div>
                  <div className="text-xs text-slate-500">{m.tagline}</div>
                </div>
                {active && <Check className="absolute right-3 top-3 h-4 w-4 text-sky-600" />}
              </button>
            );
          })}
        </div>
      </FieldWrap>

      <div className="rounded-2xl border border-sky-200/70 bg-sky-50/60 p-4 text-xs text-slate-600">
        <b className="text-slate-800">Account details later.</b> To keep this form quick and your
        banking information safe, you will enter IBAN, PayPal email or Revolut tag in{" "}
        <span className="font-semibold text-slate-800">Account → Referrals → Settings</span> once
        your application is approved and before you request your first payout.
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* STEP 4 — Review                                                        */
/* ────────────────────────────────────────────────────────────────────── */

function Step4({
  form,
  settings,
}: {
  form: UseFormReturn<ApplyForReferralInput>;
  settings: Awaited<ReturnType<typeof getReferralPublicSettings>>;
}) {
  const v = form.watch();
  const country = COUNTRY_MAP[v.country];
  const errors = form.formState.errors;
  const accepted = v.accepted_terms_version === settings.terms_version;
  const fee = new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(
    settings.payout_fee_cents / 100,
  );

  return (
    <div className="space-y-8">
      <StepHeader
        eyebrow="Almost done"
        title="Review and confirm"
        subtitle="Check your details — you'll receive a confirmation email and we'll review your application within 48 hours."
      />

      <div className="grid gap-3">
        <SummaryRow label="Name">
          {v.first_name} {v.last_name}
        </SummaryRow>
        <SummaryRow label="Phone">{v.phone}</SummaryRow>
        <SummaryRow label="Country">
          {country ? (
            <span className="inline-flex items-center gap-2">
              <span>{country.flag}</span> {country.name}
            </span>
          ) : (
            v.country
          )}
        </SummaryRow>
        <SummaryRow label="Languages">
          <div className="flex flex-wrap gap-1.5">
            {(v.languages || []).map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-1 text-xs"
              >
                {LANGUAGE_MAP[c]?.flag} {LANGUAGE_MAP[c]?.native ?? c}
              </span>
            ))}
          </div>
        </SummaryRow>
        <SummaryRow label="Channels">
          <div className="flex flex-wrap gap-1.5">
            {(v.promo_channels || []).map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-1 text-xs"
              >
                {CHANNEL_META[c].label}
              </span>
            ))}
          </div>
        </SummaryRow>
        <SummaryRow label="Payout method">{v.payout_method.toUpperCase()}</SummaryRow>
      </div>

      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-sky-200/70 bg-white/80 p-5 transition hover:bg-white">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) =>
            form.setValue(
              "accepted_terms_version",
              e.target.checked ? settings.terms_version : 0,
              { shouldValidate: true },
            )
          }
          className="mt-1 h-4 w-4 rounded border-sky-300 accent-slate-900"
        />
        <span className="text-sm text-slate-700">
          I have read and accept the{" "}
          <Link
            to="/referral/terms"
            target="_blank"
            className="font-semibold text-sky-600 underline underline-offset-4"
          >
            Partner Programme Terms (v{settings.terms_version})
          </Link>
          , including the {settings.hold_days}-day holding period and the {fee} payout processing fee.
        </span>
      </label>
      {errors.accepted_terms_version && (
        <p className="-mt-4 text-xs text-rose-400">You must accept the terms to continue.</p>
      )}
    </div>
  );
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-sky-100 bg-white/70 px-4 py-3">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="max-w-[70%] text-right text-sm text-slate-900">{children || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Shared field primitives                                                */
/* ────────────────────────────────────────────────────────────────────── */

function StepHeader({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle: string }) {
  return (
    <header>
      <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-sky-600">{eyebrow}</p>
      <h2 className="mt-2 font-display text-3xl font-black leading-tight sm:text-4xl">{title}</h2>
      <p className="mt-2 max-w-lg text-sm text-slate-500">{subtitle}</p>
    </header>
  );
}

function FieldWrap({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <label className="text-sm font-semibold text-slate-900">
          {label} {required && <span className="text-sky-600">*</span>}
        </label>
        {hint && !error && <span className="text-xs text-slate-400">{hint}</span>}
      </div>
      {children}
      {error && <p className="mt-2 text-xs text-rose-400">{error}</p>}
    </div>
  );
}

const UnderlineInput = (() => {
  function Inner(
    {
      label,
      required,
      error,
      className,
      ...rest
    }: React.InputHTMLAttributes<HTMLInputElement> & { label: string; required?: boolean; error?: string },
    ref: React.Ref<HTMLInputElement>,
  ) {
    return (
      <div className={cn("group relative", className)}>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
          {label} {required && <span className="text-sky-600">*</span>}
        </label>
        <input
          ref={ref}
          {...rest}
          className="peer w-full border-0 border-b-2 border-sky-200 bg-transparent px-0 py-2 text-base text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-slate-900"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 bg-slate-900 transition-transform duration-300 peer-focus:scale-x-100" />
        {error && <p className="mt-1 text-xs text-rose-400">{error}</p>}
      </div>
    );
  }
  return Object.assign(
    // forwardRef via a wrapper that preserves types
    (
      props: React.InputHTMLAttributes<HTMLInputElement> & {
        label: string;
        required?: boolean;
        error?: string;
      },
    ) => Inner(props as any, null),
    { displayName: "UnderlineInput" },
  );
})();

/* ────────────────────────────────────────────────────────────────────── */
/* Phone with dial-code hint                                              */
/* ────────────────────────────────────────────────────────────────────── */

const DIAL_CODES: Record<string, string> = {
  BG: "+359", RO: "+40", GR: "+30", DE: "+49", AT: "+43", FR: "+33", IT: "+39",
  ES: "+34", PT: "+351", NL: "+31", BE: "+32", PL: "+48", CZ: "+420", SK: "+421",
  HU: "+36", HR: "+385", SI: "+386", RS: "+381", MK: "+389", AL: "+355", TR: "+90",
  UK: "+44", GB: "+44", IE: "+353", DK: "+45", SE: "+46", NO: "+47", FI: "+358",
  US: "+1", CA: "+1", AU: "+61", CH: "+41", RU: "+7", UA: "+380", MD: "+373",
  BA: "+387", ME: "+382", XK: "+383", CY: "+357", MT: "+356", LU: "+352",
  EE: "+372", LV: "+371", LT: "+370",
};

function PhoneField({ form }: { form: UseFormReturn<ApplyForReferralInput> }) {
  const country = form.watch("country");
  const dial = DIAL_CODES[country] ?? "";
  const phone = form.watch("phone");
  const err = form.formState.errors.phone?.message;

  // auto-prefix dial code on first focus if empty
  const primed = useRef(false);
  useEffect(() => {
    if (!primed.current && !phone && dial) {
      primed.current = true;
    }
  }, [dial, phone]);

  return (
    <div>
      <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
        Phone <span className="text-sky-600">*</span>
      </label>
      <div className="group relative flex items-end gap-3 border-b-2 border-sky-200 transition focus-within:border-slate-900">
        {dial && (
          <span className="pb-2 font-mono text-sm text-slate-500 tabular-nums">{dial}</span>
        )}
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="88 123 4567"
          {...form.register("phone")}
          className="peer flex-1 border-0 bg-transparent px-0 py-2 text-base text-slate-900 placeholder:text-slate-400 outline-none"
        />
        <div className="pointer-events-none absolute inset-x-0 -bottom-0.5 h-0.5 origin-left scale-x-0 bg-slate-900 transition-transform duration-300 peer-focus:scale-x-100" />
      </div>
      {err && <p className="mt-1 text-xs text-rose-400">{err}</p>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Country select (dark)                                                  */
/* ────────────────────────────────────────────────────────────────────── */

function CountrySelect({
  value,
  onChange,
  error,
}: {
  value?: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const current = value ? COUNTRY_MAP[value] : null;
  return (
    <FieldWrap label="Country" required error={error}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-12 w-full items-center justify-between rounded-2xl border border-sky-200/70 bg-white px-4 text-sm text-slate-900 transition hover:border-sky-300 hover:bg-sky-50"
          >
            <span className="flex items-center gap-2 truncate">
              {current ? (
                <>
                  <span className="text-base leading-none">{current.flag}</span>
                  <span className="truncate">{current.name}</span>
                  {current.eu && (
                    <span className="ml-1 rounded-full bg-sky-200 px-1.5 py-0.5 text-[10px] font-bold text-sky-600">EU</span>
                  )}
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-400">Select country</span>
                </>
              )}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] border-sky-200/70 bg-white p-0 text-slate-900" align="start">
          <Command className="bg-transparent">
            <CommandInput placeholder="Search country…" className="text-slate-900 placeholder:text-slate-400" />
            <CommandList>
              <CommandEmpty>No match.</CommandEmpty>
              <CommandGroup heading="European Union">
                {COUNTRIES.filter((c) => c.eu).map((c) => (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} ${c.code}`}
                    onSelect={() => { onChange(c.code); setOpen(false); }}
                    className="text-slate-900 data-[selected=true]:bg-sky-100"
                  >
                    <span className="mr-2">{c.flag}</span>
                    <span className="flex-1">{c.name}</span>
                    {value === c.code && <Check className="h-4 w-4 text-emerald-400" />}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandGroup heading="Rest of the world">
                {COUNTRIES.filter((c) => !c.eu).map((c) => (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} ${c.code}`}
                    onSelect={() => { onChange(c.code); setOpen(false); }}
                    className="text-slate-900 data-[selected=true]:bg-sky-100"
                  >
                    <span className="mr-2">{c.flag}</span>
                    <span className="flex-1">{c.name}</span>
                    {value === c.code && <Check className="h-4 w-4 text-emerald-400" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FieldWrap>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Language multi-select                                                  */
/* ────────────────────────────────────────────────────────────────────── */

function LanguageMultiSelect({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => value.map((c) => LANGUAGE_MAP[c]).filter(Boolean), [value]);

  function toggle(code: string) {
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  }

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex min-h-12 w-full flex-wrap items-center gap-2 rounded-2xl border border-sky-200/70 bg-white px-3 py-2 text-left text-sm text-slate-900 transition hover:border-sky-300 hover:bg-sky-50"
          >
            {selected.length === 0 && (
              <span className="px-1 text-slate-400">Select languages…</span>
            )}
            {selected.map((l) => (
              <span
                key={l.code}
                className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 px-2.5 py-1 text-xs font-semibold text-sky-700"
                onClick={(e) => { e.stopPropagation(); toggle(l.code); }}
              >
                <span>{l.flag}</span>
                {l.native}
                <X className="h-3 w-3" />
              </span>
            ))}
            <ChevronsUpDown className="ml-auto h-4 w-4 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] border-sky-200/70 bg-white p-0 text-slate-900" align="start">
          <Command className="bg-transparent">
            <CommandInput placeholder="Search language…" className="text-slate-900 placeholder:text-slate-400" />
            <CommandList>
              <CommandEmpty>No match.</CommandEmpty>
              <CommandGroup>
                {LANGUAGES.map((l) => {
                  const active = value.includes(l.code);
                  return (
                    <CommandItem
                      key={l.code}
                      value={`${l.name} ${l.native}`}
                      onSelect={() => toggle(l.code)}
                      className="text-slate-900 data-[selected=true]:bg-sky-100"
                    >
                      <span className="mr-2">{l.flag}</span>
                      <span className="flex-1">{l.native}</span>
                      <span className="mr-2 text-xs text-slate-400">{l.name}</span>
                      {active && <Check className="h-4 w-4 text-emerald-400" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Channel chips                                                          */
/* ────────────────────────────────────────────────────────────────────── */

function ChannelChips({
  value,
  onChange,
}: {
  value: readonly string[];
  onChange: (v: string[]) => void;
}) {
  function toggle(c: string) {
    onChange(value.includes(c) ? value.filter((x) => x !== c) : [...value, c]);
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {PROMO_CHANNELS.map((c) => {
        const meta = CHANNEL_META[c];
        const Icon = meta.icon;
        const active = value.includes(c);
        return (
          <button
            key={c}
            type="button"
            onClick={() => toggle(c)}
            aria-pressed={active}
            className={cn(
              "group relative flex items-center gap-3 overflow-hidden rounded-2xl border p-3 text-left transition",
              active
                ? "border-slate-900 bg-white shadow-[0_0_0_3px_rgba(15,23,42,0.08)]"
                : "border-sky-200/70 bg-white/80 hover:border-sky-300 hover:bg-sky-50",
            )}
          >
            <span
              className={cn(
                "grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br text-slate-900 transition group-hover:scale-105",
                meta.hue,
                !active && "opacity-70",
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="flex-1 text-sm font-semibold">{meta.label}</span>
            {active && (
              <Check className="h-4 w-4 text-sky-600" />
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Payout method brand marks (inline SVG, monochrome, unified sizing)     */
/* ────────────────────────────────────────────────────────────────────── */

function BankLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 32" className={className} aria-label="Bank transfer" role="img" fill="none">
      <g fill="currentColor">
        <path d="M14 4 4 10v2h20v-2L14 4Z" />
        <rect x="6" y="14" width="2.6" height="9" />
        <rect x="12.7" y="14" width="2.6" height="9" />
        <rect x="19.4" y="14" width="2.6" height="9" />
        <rect x="4" y="24.5" width="20" height="2.2" />
      </g>
      <text x="32" y="22" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="14" fontWeight="800" letterSpacing="0.5" fill="currentColor">SEPA</text>
    </svg>
  );
}

function PayPalLogo({ className }: { className?: string }) {
  // Simplified wordmark rendered as text in the same brand-shape (two overlapping "P"s implied)
  return (
    <svg viewBox="0 0 140 32" className={className} aria-label="PayPal" role="img" fill="none">
      <text x="0" y="23" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="22" fontWeight="900" letterSpacing="-0.5" fill="#003087">Pay</text>
      <text x="46" y="23" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="22" fontWeight="900" letterSpacing="-0.5" fill="#009cde">Pal</text>
    </svg>
  );
}

function WiseLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 90 32" className={className} aria-label="Wise" role="img" fill="none">
      <rect x="0" y="4" width="90" height="24" rx="4" fill="#9FE870" />
      <text x="10" y="23" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="18" fontWeight="900" fill="#163300">wise</text>
    </svg>
  );
}

function RevolutLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 130 32" className={className} aria-label="Revolut" role="img" fill="none">
      <text x="0" y="23" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="20" fontWeight="900" letterSpacing="-0.5" fill="currentColor">Revolut</text>
    </svg>
  );
}
